import {
  darkThemeStyles,
  isValidDayOfYear,
  JupiterTheme,
  useAccounts,
} from '@jupiterone/jupiter-web-toolkit';
import { Button, TextField } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { SyntheticEvent, useState } from 'react';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    ...darkThemeStyles,
    form: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    textContainer: {
      width: '100%',
      maxWidth: 400,
    },
    button: {
      marginTop: theme.spacing(5),
    },
  })
);

export type ApiKeyFormValues = {
  apiKeyName: string;
  expireInDays: string;
};

interface Props {
  onSubmit: (values: ApiKeyFormValues) => void;
}

function isNumberBetween(stringNumber: string, min: number, max: number) {
  const parsedNumber = parseInt(stringNumber, 10);

  if (parsedNumber) {
    return parsedNumber > min && parsedNumber <= max;
  }

  return false;
}

export const MAX_FREE_TRIAL_API_KEY_EXPIRATION = 30;
export const MAX_PAID_ACCOUNT_API_KEY_EXPIRATION = 365;

const NewApiKeyForm = ({ onSubmit }: Props) => {
  const classes = useStyles();
  const { account } = useAccounts();
  const [apiKeyName, setApiKeyName] = useState<string>('');
  const [expireInDays, setExpireInDays] = useState<string>('');
  const [error, setError] = useState<string>('');
  const isFreeAccount = account?.type === 'FREE';

  const maxExpirationDays = isFreeAccount
    ? MAX_FREE_TRIAL_API_KEY_EXPIRATION
    : MAX_PAID_ACCOUNT_API_KEY_EXPIRATION;

  const ERROR_TEXT: string = `Enter a number between 1 and ${maxExpirationDays}`;

  const handleSetApiKeyName = (event: any) => {
    setApiKeyName(event.target.value);
  };

  const handleSetDaysBeforeExpiration = (event: any) => {
    const value = event.target.value;
    if (!isValidDayOfYear(value)) {
      setError(ERROR_TEXT);
    } else if (isFreeAccount && !isNumberBetween(value, 0, maxExpirationDays)) {
      setError(ERROR_TEXT);
    } else {
      setError('');
    }
    setExpireInDays(event.target.value);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const values = { apiKeyName, expireInDays };
    onSubmit(values);
  };

  const buttonDisabled = error !== '' || !apiKeyName || !expireInDays;

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <div className={classes.textContainer}>
        <TextField
          label="Key name"
          value={apiKeyName}
          InputLabelProps={{
            className: classes.textInputLabel,
            classes: {
              disabled: classes.textInputLabelDisabled,
              focused: classes.textInputLabelFocused,
            },
          }}
          InputProps={{
            className: classes.textInput,
            classes: {
              disabled: classes.disabled,
              underline: classes.textFieldUnderline,
            },
          }}
          margin="normal"
          fullWidth={true}
          onChange={handleSetApiKeyName}
        />
        <TextField
          label="Days before expiration"
          value={expireInDays}
          InputLabelProps={{
            className: classes.textInputLabel,
            classes: {
              disabled: classes.textInputLabelDisabled,
              focused: classes.textInputLabelFocused,
            },
          }}
          InputProps={{
            className: classes.textInput,
            classes: {
              disabled: classes.disabled,
              underline: classes.textFieldUnderline,
            },
          }}
          placeholder={`1 - ${maxExpirationDays}`}
          margin="normal"
          fullWidth={true}
          onChange={handleSetDaysBeforeExpiration}
          helperText={error}
          error={error !== ''}
        />
      </div>
      {!buttonDisabled && (
        <Button
          className={classes.button}
          color="primary"
          variant="outlined"
          type="submit"
        >
          Create
        </Button>
      )}
    </form>
  );
};

export default NewApiKeyForm;
